import { DATE_FORMATS, formatDate } from "@/utils";

/**
 * Domain logic for appraisal recommendations.
 *
 * A recommendation is built from one or more sections. The rules are:
 *   - `DISMISSAL` is exclusive — it cannot be combined with anything.
 *   - `INCENTIVE` may be paired with exactly one of `PROMOTION` / `INCREMENT`.
 *   - `PROMOTION` and `INCREMENT` are mutually exclusive.
 *
 * Combined selections are encoded as underscore-joined strings, so the only
 * valid combination types are `INCENTIVE_PROMOTION` and `INCENTIVE_INCREMENT`.
 */

export type RecommendationSection =
  | "DISMISSAL"
  | "INCENTIVE"
  | "PROMOTION"
  | "INCREMENT";

export type RecommendationType =
  | RecommendationSection
  | "INCENTIVE_PROMOTION"
  | "INCENTIVE_INCREMENT"
  | null;

export const RECOMMENDATION_SECTIONS: {
  key: RecommendationSection;
  label: string;
}[] = [
  { key: "DISMISSAL", label: "Dismissal" },
  { key: "INCENTIVE", label: "Incentive" },
  { key: "PROMOTION", label: "Promotion" },
  { key: "INCREMENT", label: "Increment" },
];

/** Split a (possibly combined) recommendation type into its active sections. */
export const getRecommendationSections = (
  type?: RecommendationType | string | null,
): RecommendationSection[] =>
  type ? (type.split("_") as RecommendationSection[]) : [];

/**
 * Combine active sections back into the canonical recommendation type.
 * `INCENTIVE` is always written first so the value round-trips with
 * {@link getRecommendationSections}.
 */
export const combineRecommendationSections = (
  sections: RecommendationSection[],
): RecommendationType => {
  if (sections.includes("INCENTIVE") && sections.includes("PROMOTION"))
    return "INCENTIVE_PROMOTION";
  if (sections.includes("INCENTIVE") && sections.includes("INCREMENT"))
    return "INCENTIVE_INCREMENT";
  return sections[0] ?? null;
};

/**
 * Toggle a section on/off and return the resulting recommendation type,
 * enforcing the combination rules described above.
 */
export const toggleRecommendationSection = (
  current: RecommendationType | string | null,
  key: RecommendationSection,
): RecommendationType => {
  const active = getRecommendationSections(current);

  // Turning the section off.
  if (active.includes(key))
    return combineRecommendationSections(active.filter((s) => s !== key));

  // Dismissal replaces any existing selection.
  if (key === "DISMISSAL") return "DISMISSAL";

  // Any reward section drops dismissal and the conflicting reward.
  let next = active.filter((s) => s !== "DISMISSAL");
  if (key === "PROMOTION") next = next.filter((s) => s !== "INCREMENT");
  if (key === "INCREMENT") next = next.filter((s) => s !== "PROMOTION");
  return combineRecommendationSections([...next, key]);
};

/**
 * Whether selecting a section would violate the combination rules given the
 * currently active sections (used to disable the toggle in the UI).
 */
export const isRecommendationSectionDisabled = (
  current: RecommendationType | string | null,
  key: RecommendationSection,
): boolean => {
  const active = getRecommendationSections(current);
  if (active.length === 0 || active.includes(key)) return false;
  if (active.includes("DISMISSAL")) return true; // dismissal is exclusive
  if (key === "DISMISSAL") return true; // can't add dismissal to a reward
  if (key === "PROMOTION") return active.includes("INCREMENT");
  if (key === "INCREMENT") return active.includes("PROMOTION");
  return false;
};

const formatMonth = (date: unknown) =>
  date ? formatDate(date as never, DATE_FORMATS.ISO_DATE) : undefined;

/** Payload fields contributed by a single recommendation section. */
const getSectionFields = (
  section: RecommendationSection,
  values: any,
): Record<string, unknown> => {
  switch (section) {
    case "DISMISSAL":
      return { last_working_day: formatMonth(values.last_working_day) };
    case "INCENTIVE":
      return {
        incentive_type: values.incentive_type,
        incentive_value: Number(values.incentive_value),
        incentive_base: values.incentive_base,
        incentive_effective_month: formatMonth(values.incentive_effective_month),
      };
    case "PROMOTION":
      return {
        suggested_designation_id: values.suggested_designation_id,
        new_salary: values.new_salary,
        promotion_effective_month: formatMonth(
          values.promotion_effective_month,
        ),
      };
    case "INCREMENT":
      return {
        increment_type: values.increment_type,
        increment_value: Number(values.increment_value),
        increment_base: values.increment_base,
        increment_effective_month: formatMonth(
          values.increment_effective_month,
        ),
      };
  }
};

/**
 * Build the merged payload fields for every active section of a recommendation
 * type (supports both single and combined types).
 */
export const getRecommendationFields = (
  type: RecommendationType | string | null | undefined,
  values: any,
): Record<string, unknown> =>
  getRecommendationSections(type).reduce(
    (acc, section) => ({ ...acc, ...getSectionFields(section, values) }),
    {} as Record<string, unknown>,
  );
